const LEVEL_VALUES = {
    trace: 10,
    debug: 20,
    info: 30,
    warn: 40,
    error: 50,
    fatal: 60,
};
const LEVEL_METHODS = {
    trace: 'debug',
    debug: 'debug',
    info: 'info',
    warn: 'warn',
    error: 'error',
    fatal: 'error',
};
const LEVEL_COLORS = {
    trace: 'color: gray',
    debug: 'color: cyan',
    info: 'color: green',
    warn: 'color: orange',
    error: 'color: red',
    fatal: 'color: red; font-weight: bold',
};
// WebSocket for sending logs
let logSocket = null;
let sendToServer = false;
let logBuffer = [];
// HTTP fallback configuration
const HTTP_LOG_ENDPOINT = '/api/logs';
const HTTP_FLUSH_INTERVAL = 5000; // Flush buffered logs every 5 seconds
const HTTP_FLUSH_THRESHOLD = 10; // Flush when buffer exceeds this size
let httpFlushTimer = null;
let isFlushingViaHttp = false;
// Global log level override (for dev mode)
let globalLogLevel = null;
/**
 * Set global log level override (for dev mode)
 * When set, all loggers will use this level regardless of their config
 */
export function setGlobalLogLevel(level) {
    globalLogLevel = level;
}
/**
 * Get the current global log level
 */
export function getGlobalLogLevel() {
    return globalLogLevel;
}
/**
 * Enable/disable sending logs to server
 */
export function enableServerLogging(enable = true) {
    sendToServer = enable;
    if (enable) {
        startHttpFlushTimer();
    }
    else {
        stopHttpFlushTimer();
    }
}
/**
 * Set the WebSocket to use for log forwarding
 */
export function setLogSocket(socket) {
    logSocket = socket;
    // Flush any buffered logs when socket connects
    if (socket && socket.readyState === WebSocket.OPEN) {
        flushLogBuffer();
    }
}
/**
 * Flush buffered logs to the server via WebSocket
 */
function flushLogBuffer() {
    if (!logSocket || logSocket.readyState !== WebSocket.OPEN)
        return;
    while (logBuffer.length > 0) {
        const log = logBuffer.shift();
        if (log) {
            try {
                logSocket.send(JSON.stringify({
                    type: 'CLIENT_LOG',
                    ...log,
                }));
            }
            catch {
                // Re-add to buffer if send failed
                logBuffer.unshift(log);
                break;
            }
        }
    }
}
/**
 * Flush buffered logs to the server via HTTP
 * Used as fallback when WebSocket is not available
 */
async function flushLogBufferViaHttp() {
    // Don't flush if already flushing or no logs
    if (isFlushingViaHttp || logBuffer.length === 0)
        return;
    // Don't flush via HTTP if WebSocket is available
    if (logSocket && logSocket.readyState === WebSocket.OPEN) {
        flushLogBuffer();
        return;
    }
    isFlushingViaHttp = true;
    const logsToSend = logBuffer.splice(0, 50); // Take up to 50 logs
    try {
        const response = await fetch(HTTP_LOG_ENDPOINT, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                logs: logsToSend.map(log => ({
                    level: log.level,
                    message: log.message,
                    context: log.data,
                })),
            }),
            // Use keepalive for critical logs during page unload
            keepalive: true,
        });
        if (!response.ok) {
            // Re-add logs to buffer if request failed
            logBuffer.unshift(...logsToSend);
        }
    }
    catch {
        // Re-add logs to buffer if request failed
        logBuffer.unshift(...logsToSend);
    }
    finally {
        isFlushingViaHttp = false;
    }
}
/**
 * Start the HTTP flush timer
 */
function startHttpFlushTimer() {
    if (httpFlushTimer)
        return;
    httpFlushTimer = setInterval(() => {
        flushLogBufferViaHttp();
    }, HTTP_FLUSH_INTERVAL);
}
/**
 * Stop the HTTP flush timer
 */
function stopHttpFlushTimer() {
    if (httpFlushTimer) {
        clearInterval(httpFlushTimer);
        httpFlushTimer = null;
    }
}
/**
 * Send critical error immediately via HTTP
 */
function sendCriticalErrorViaHttp(level, message, data) {
    // Use fetch with keepalive for immediate delivery
    try {
        fetch(HTTP_LOG_ENDPOINT, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                logs: [{
                        level,
                        message,
                        context: data,
                    }],
            }),
            keepalive: true,
        }).catch(() => {
            // Ignore errors - this is a best-effort delivery
        });
    }
    catch {
        // Ignore errors
    }
}
/**
 * Send a log entry to the server via WebSocket (with HTTP fallback)
 */
function sendLog(level, message, data) {
    if (!sendToServer)
        return;
    const entry = {
        level,
        message,
        data,
        timestamp: Date.now(),
    };
    // Send critical errors (error/fatal) immediately via HTTP as backup
    if (level === 'error' || level === 'fatal') {
        sendCriticalErrorViaHttp(level, message, data);
    }
    if (logSocket && logSocket.readyState === WebSocket.OPEN) {
        try {
            logSocket.send(JSON.stringify({
                type: 'CLIENT_LOG',
                ...entry,
            }));
        }
        catch {
            // Buffer if send failed
            logBuffer.push(entry);
            // Keep buffer size reasonable
            if (logBuffer.length > 100) {
                logBuffer = logBuffer.slice(-50);
            }
        }
    }
    else {
        // Buffer until socket is available
        logBuffer.push(entry);
        // Keep buffer size reasonable
        if (logBuffer.length > 100) {
            logBuffer = logBuffer.slice(-50);
        }
        // Trigger HTTP flush if buffer exceeds threshold
        if (logBuffer.length >= HTTP_FLUSH_THRESHOLD) {
            flushLogBufferViaHttp();
        }
    }
}
/**
 * Create a browser-compatible logger
 */
export function createBrowserLogger(config) {
    const minLevel = LEVEL_VALUES[config.level];
    const baseContext = { component: config.component };
    // Enable server logging by default in dev mode
    if (typeof window !== 'undefined') {
        enableServerLogging(true);
    }
    return createLogger(minLevel, baseContext);
}
function createLogger(configuredMinLevel, baseContext) {
    const createLogMethod = (level) => {
        return (contextOrMessage, message) => {
            // Use global level if set (dev mode), otherwise use configured level
            const minLevel = globalLogLevel !== null ? LEVEL_VALUES[globalLogLevel] : configuredMinLevel;
            if (LEVEL_VALUES[level] < minLevel)
                return;
            const method = LEVEL_METHODS[level];
            const color = LEVEL_COLORS[level];
            const timestamp = new Date().toISOString();
            let logMessage;
            let mergedContext;
            if (typeof contextOrMessage === 'string') {
                logMessage = contextOrMessage;
                mergedContext = baseContext;
                console[method](`%c[${timestamp}] [${level.toUpperCase()}] [${baseContext.component}]`, color, contextOrMessage);
            }
            else {
                logMessage = message ?? '';
                mergedContext = { ...baseContext, ...contextOrMessage };
                console[method](`%c[${timestamp}] [${level.toUpperCase()}] [${mergedContext.component}]`, color, message, mergedContext);
            }
            // Send to server via WebSocket (info level and above)
            if (sendToServer && LEVEL_VALUES[level] >= LEVEL_VALUES.info) {
                sendLog(level, logMessage, mergedContext);
            }
        };
    };
    return {
        trace: createLogMethod('trace'),
        debug: createLogMethod('debug'),
        info: createLogMethod('info'),
        warn: createLogMethod('warn'),
        error: createLogMethod('error'),
        fatal: createLogMethod('fatal'),
        child: (context) => createLogger(configuredMinLevel, { ...baseContext, ...context }),
    };
}
//# sourceMappingURL=browser.js.map